import React, { useState, useEffect, useRef } from "react";
import Editor, { Monaco } from "@monaco-editor/react";
import { ICodeData } from "../../types";
import { useMonacoExternalValue } from "../../hook/useMonacoExternalValue";
import { useYMonacoBinding } from "./useYMonacoBinding";
import { useFlowContext } from "../../providers/FlowProvider";
import { registerRunNodeAction } from "./runNodeMonacoAction";
import { describeError } from "../../adapters/node/autkRunSettlement";
import { ReferenceStrip } from "./widgets/WidgetTag";
import { insertReference, useCodeReferences } from "./widgets/monacoCodeReferences";
import { useEditorTooltip } from "./useEditorTooltip";
import type { ChipPreviewSource } from "./widgets/chipPreviewSource";
import type { CodeLanguage, InputScope, ReferenceScope } from "../../utils/references/codeReferences";
import { NOTEBOOK_SPEC_EDITOR_MAX, useNotebookEditorHeight } from "./useNotebookEditorHeight";
import { nodeEditorLook } from "./nodeEditorLook";
import { NODE_EDITOR_SCROLLING } from "./nodeEditorScrolling";

const NO_REFERENCES: ReferenceScope = { widgets: [], inputs: [] };
const NO_INPUTS: InputScope[] = [];

type GrammarEditorProps = {
    output: ICodeData;
    nodeId: string;
    applyGrammar?: any;
    replacedCode: string; // code with all marks resolved
    sendCodeToWidgets: any;
    replacedCodeDirty: number; // a count of the sends: a change applies the grammar
    defaultValue?: any;
    floatCode?: any;
    readOnly: boolean;
    /** Lets a rejected applyGrammar become an error output, so the run ends (#271). */
    setOutputCallback?: (output: { code: string; content: string }) => void;
    /** #662: what the node's references name: its widgets and its inputs. */
    references?: ReferenceScope;
    /** The inputs whose tags sit above the editor, with the widgets'. */
    stripInputs?: InputScope[];
    /** Read an input's columns for its tags. */
    onLoadColumns?: (slot: number) => void;
    widgetLanguage?: CodeLanguage;
    /** The spec's grammar where its chips write a field escaped (`VEGA_LITE`),
     * which also gives the header the `inputs` tag and the scenarios' scales. */
    referenceGrammar?: string;
    /** What its chips' and tags' hovers preview (chipPreviewSource). */
    chipPreviews?: ChipPreviewSource | null;
    /** A run's spec put back from the provenance drawer; each new one is
     *  written in, even when it is the spec the editor loaded with. */
    restoredCode?: { code: string; nonce: number } | null;
};

export default function GrammarEditor({
    output,
    nodeId,
    applyGrammar,
    replacedCode,
    sendCodeToWidgets,
    replacedCodeDirty,
    defaultValue,
    floatCode,
    readOnly,
    setOutputCallback,
    references = NO_REFERENCES,
    stripInputs = NO_INPUTS,
    onLoadColumns,
    widgetLanguage = "json",
    referenceGrammar,
    chipPreviews = null,
    restoredCode = null,
}: GrammarEditorProps) {
    // The spec the node holds from the first render: what this floats into the
    // node's code at mount. Floated as the editor's empty "{}", the node's code
    // read "{}" until the spec came in, and a save then (the one that makes a
    // dataflow loaded from a file a project) kept "{}" as the node's spec.
    const [grammar, _setGrammar] = useState<string>(() => (typeof defaultValue === "string" ? defaultValue : "{}"));
    // #662: the mounted editor, for the reference tags and chips. A
    // reference is not JSON until it is resolved, so the errors it causes are hidden.
    const [widgetEditor, setWidgetEditor] = useState<{ editor: any; monaco: any } | null>(null);
    useCodeReferences(widgetEditor?.editor, widgetEditor?.monaco, references, widgetLanguage, {
        hideJsonMarkers: true,
    });
    // Its hovers, Monaco's own being off, in the Curio tooltip.
    useEditorTooltip(widgetEditor?.editor, widgetEditor?.monaco, references, widgetLanguage, chipPreviews);
    // In a notebook cell the spec is as tall as its lines, over its output.
    const editorHeight = useNotebookEditorHeight(NOTEBOOK_SPEC_EDITOR_MAX);
    const grammarRef = useRef(grammar);
    const setGrammar = (data: string) => {
        grammarRef.current = data;
        _setGrammar(data);
    };

    const replacedCodeDirtyBypass = useRef(false);

    // The Monaco model is the source of truth while the user types; `grammar`
    // only mirrors it. Content flows INTO the editor exclusively through this
    // hook: `defaultValue` is applied only when a genuinely new external
    // string arrives, and `undefined`/stale flips are ignored - the previous
    // unconditional `setGrammar(defaultValue)` effect is what reset a fresh
    // Autark node to the default spec while typing. That subsumes the older
    // `!= undefined` guard this effect used to carry (#157). `baselineRef` is
    // the as-loaded grammar: an editor holding something else holds the
    // user's typing.
    const { baselineRef, applyValue, attachEditor } = useMonacoExternalValue({
        externalValue: defaultValue,
        initialContent: "{}",
        onExternalApply: (value) => {
            setGrammar(value);
            // Same call CodeEditor makes, and for the same reason: WidgetsEditor
            // only recomputes its marker list when `markersDirty` changes, and
            // this is the only thing that changes it outside a run. Without it a
            // grammar spec's `[!! ... !!]` markers render no widgets until the
            // node has been played once.
            sendCodeToWidgets(value);
        },
    });

    // A run's spec put back from the provenance drawer, as CodeEditor puts
    // back a run's code: written in even when it equals the loaded spec.
    useEffect(() => {
        if (!restoredCode) return;
        applyValue(restoredCode.code);
        setGrammar(restoredCode.code);
        sendCodeToWidgets(restoredCode.code);
    }, [restoredCode]);

    // The node's spec in the dataflow's doc, typed together, as in the code
    // editor.
    useYMonacoBinding(widgetEditor?.editor, widgetEditor?.monaco, nodeId, {
        canWrite: !readOnly,
        baselineRef,
    });

    // onMount fires once, so the action reads the CURRENT play function through
    // a ref rather than capturing the first render's (#223).
    const { playNodesUpTo, dashboardOn } = useFlowContext();
    // The spec in a plain gray box, as in a notebook, except on a dashboard tile.
    const look = nodeEditorLook(!!dashboardOn);
    const runNodeRef = useRef<() => void>(() => {});
    runNodeRef.current = () => playNodesUpTo(nodeId);

    const handleEditorMount = (editor: any, monaco: Monaco) => {
        // Vega-Lite specs carry `$schema: "https://.../v6.json"` (~2 MB). Monaco's
        // built-in JSON support will fetch and validate against that URL on first
        // tab-switch into the editor, which freezes the main thread while it
        // resolves the schema graph. Disable URL fetching and clear the schema
        // list so Monaco only does the cheap structural JSON parse.
        try {
            // monaco-editor's core typings stub `languages.json` as
            // `{ deprecated: true }`; the real declarations ship with the JSON
            // language contribution, whose .d.ts is an empty `export {}`. The
            // API exists at runtime once that contribution is loaded, so this
            // states the one call being made rather than reaching for `any`.
            const jsonLanguage = monaco.languages.json as unknown as {
                jsonDefaults: {
                    setDiagnosticsOptions(options: {
                        validate?: boolean;
                        enableSchemaRequest?: boolean;
                        schemaRequest?: "error" | "warning" | "ignore";
                        schemas?: unknown[];
                    }): void;
                };
            };
            jsonLanguage.jsonDefaults.setDiagnosticsOptions({
                validate: true,
                enableSchemaRequest: false,
                // With fetching off, a `$schema` that cannot be loaded is not a
                // problem with the spec; left unset, Monaco reported it as a
                // warning squiggle under every valid Vega-Lite URL (#494).
                schemaRequest: "ignore",
                schemas: [],
            });
        } catch {
            // Defensive: older Monaco builds without languages.json — no-op.
        }
        attachEditor(editor);
        setWidgetEditor({ editor, monaco });
        editorHeight.attach(editor, monaco);
        // Same chord as the code editor, so a grammar node runs the way a
        // Python one does (#223).
        registerRunNodeAction(editor, monaco, () => runNodeRef.current);
    };

    useEffect(() => {
        // Strings only: floating a non-string (the old reset chain produced
        // `undefined`) poisons ``nodeState.code``/``data.code`` downstream.
        if (floatCode != undefined && typeof grammar === "string") floatCode(grammar);
    }, [grammar]);

    useEffect(() => {
        if (
            replacedCode != "" &&
            replacedCodeDirtyBypass.current &&
            output.code == "exec" &&
            applyGrammar != undefined
        ) {
            // Catch, don't float (#201). An escaped rejection reaches
            // `window` as `unhandledrejection`, which webpack-dev-server's
            // runtime-error overlay listens for - so one throw inside the
            // grammar took the whole screen in development.
            void Promise.resolve(applyGrammar(replacedCode)).catch((err) => {
                console.error("[GrammarEditor] applyGrammar failed:", err);
                // A rejection that only reached the console left the node at
                // "exec" forever, and with it the Run All guard (#271).
                setOutputCallback?.({
                    code: "error",
                    // Never an empty string: a node in Error with no text tells
                    // the user nothing (#318).
                    content: describeError(err),
                });
            });
        }
        replacedCodeDirtyBypass.current = true;
    }, [replacedCodeDirty]);

    const updateGrammarContent = (value: string, readOnly: boolean) => {
        if (!readOnly) setGrammar(value);
    };

    return (
        <div
            id={"vega-editor_" + nodeId}
            className="my-editor nowheel nodrag"
            style={{ height: editorHeight.inCell ? "auto" : "100%", display: "flex", flexDirection: "column" }}
        >
            <ReferenceStrip
                nodeId={nodeId}
                widgets={references.widgets}
                inputs={stripInputs}
                selections={references.selections}
                language={widgetLanguage}
                grammar={referenceGrammar}
                disabled={readOnly}
                onInsert={(inner) => insertReference(widgetEditor?.editor, inner)}
                onLoadColumns={onLoadColumns}
                previews={chipPreviews}
            />
            <div className={look.wrapperClassName} style={{ flex: 1, minHeight: 0, ...editorHeight.wrapperStyle }}>
                {/* Uncontrolled on purpose: a per-keystroke `value` round-trip
                    lets a render that lands with a stale string do a full-model
                    replace — resetting content and throwing the cursor to the
                    end (dev/70). External content arrives via attachEditor /
                    applyValue in useMonacoExternalValue instead. */}
                <Editor
                    height="100%"
                    language="json"
                    theme="vs"
                    path={`grammar-${nodeId}.json`}
                    defaultValue="{}"
                    onChange={(value) => updateGrammarContent(value ?? "{}", readOnly)}
                    onMount={handleEditorMount}
                    options={{
                        fontSize: 13,
                        fontFamily: "'Source Code Pro', Consolas, 'Courier New', monospace",
                        minimap: { enabled: false },
                        readOnly: readOnly,
                        scrollBeyondLastLine: false,
                        formatOnType: true,
                        autoClosingBrackets: "always",
                        // As in CodeEditor: never wrapped, and each bar at rest
                        // whenever the spec runs past its side.
                        ...NODE_EDITOR_SCROLLING,
                        // A plain input box, but on a dashboard tile.
                        ...look.editorOptions,
                        // As in CodeEditor: the Curio tooltip shows the hovers.
                        hover: { enabled: false },
                    }}
                />
            </div>
        </div>
    );
}
